/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import _ from "underscore";
import util from "../util/util.js";
import h from "util/dom-builder.js";
import '../ui/quiz.js';
import googleMaps from '../util/google-maps.js';
import mapStyles from '../map/styles.js';
import markerLayer from "../map/marker-layer.js";
import UTMGridMapType from "../map/utm-grid-layer.js";
import * as utmGrid from "../map/utm-grid.js";
import sharedSitesMap from "../map/shared-sites.js";

import programs from '../programs.js';

import "../util/knockout-pikaday.js";
import "./shared/location-search.js";

let priorityColor = 'orange';
let nonPriorityColor = 'dodgerblue';
let unavailableColor = '#999'

var BookingMapViewModel = function(params) {
	var self = this;

	self.priorityColor = priorityColor;
	self.nonPriorityColor = nonPriorityColor;
	self.unavailableColor = unavailableColor;

	// Set default params
	params = _.defaults(params, {
	});

	var queryParams = util.queryParams();
	var eventCode = queryParams.event;
	var today = util.formatISODate(new Date());

	self.bookingDate = ko.observable();
	self.map = ko.observable();
	self.mapOptions = ko.observable();
	self.mapBounds = ko.observable();
	self.bookingDateFocus = ko.observable(false);
	self.formattedBookingDate = ko.pureComputed(function() {
		var date = self.bookingDate();
		if(!date) {
			return '';
		}
		date = new Date(date);
		var formatted = date.toLocaleDateString("en-AU", { year: "numeric", month: "2-digit", day: "2-digit" });
		var day = date.toLocaleString(undefined, {weekday: 'short' });

		return day + " " + formatted;
	});
	self.maxDate = ko.observable();
	self.minDate = ko.observable();
	self.loaded = ko.observable(false);
	self.program = ko.observable();

	self.prioritySiteIds = new Set();
	self.siteIds = new Set();

	self.siteAvailability = new Set();

	self.eventName = ko.observable('');

	self.isGridBased = ko.pureComputed(function() {
		return !!(self.program() && self.program().mapGrid);
	});

	// For location search
	self.sharedSitesPromise = new Promise(function(resolve, reject) {
		self.sharedSitesPromiseResolver = resolve;
	});

	// Maximum zoom level at which markers are displayed.
	function markerMaxZoom() {
		return 11;
	}

	function isPrioritySite(id) {
		return self.prioritySiteIds.has(id);
	}

	function isEventSite(id) {
		return self.siteIds.size == 0 || self.siteIds.has(id);
	}

	function isSiteAvailableOnDate(siteID, date) {
		return !self.siteAvailability.has(siteID + ":" + date) && !self.siteAvailability.has(siteID + ":*");
	}

	function userBookingForSite(siteID) {
		return self.event && self.event.participation.bookings.filter(b => (b.cell_id || b.site_id) == siteID);
	}

	function userHasBookedSiteOnDate(siteID, date) {
		return userBookingForSite(siteID).filter(b => b.date == date).length > 0;
	}

	self.configureDatePicker = function(picker) {
		if(picker) {
			self.maxDate.subscribe(function(date) {
				if(date) {
					picker.setMaxDate(util.parseISODate(date));
				}
			});
			self.minDate.subscribe(function(date) {
				if(!date || date < today) {
					date = today;
				}
				if(date) {
					picker.setMinDate(util.parseISODate(date));
				}
			});
		}
	}

	var eventPromise = birdata.event(eventCode);

	Promise.all([
		eventPromise,
		eventPromise.then(e => birdata.eventSiteAvailability(e.id)),
		birdata.lookup(),
		programs()])
	.then(function([event, siteAvailability, lookup, programs]) {
		self.event = event;
		self.eventName(event.name);
		self.program(programs.find(event.program));

		self.minDate(event.start_date);
		self.maxDate(event.finish_date);

		var start = event.start_date;

		if(event.priority_shared_site_ids) {
			self.prioritySiteIds = new Set(event.priority_shared_site_ids);
		} else if(event.priority_site_ids) {
			self.prioritySiteIds = new Set(event.priority_site_ids.split(/\s+/).filter(x => x));
		}

		if(event.shared_site_ids) {
			self.siteIds = new Set(event.shared_site_ids);
		} else if(event.site_ids) {
			self.siteIds = new Set(event.site_ids.split(/\s+/).filter(x => x))
		}

		self.siteAvailability = new Set(siteAvailability);

		self.bookingDate(today > start ? today : start);
		self.loaded(true);
	});

	function isDateDuringEvent(date) {
		return date >= today && date >= self.minDate() && date <= self.maxDate()
	}

	function adjustedDate(date, deltaDays) {
		var date = util.parseISODate(date);
		date.setDate(date.getDate() + deltaDays);
		date = util.formatISODate(date);
		return date;
	}

	self.changeDate = function(deltaDays) {
		var date = adjustedDate(self.bookingDate(), deltaDays);
		if(isDateDuringEvent(date)) {
			self.bookingDate(date);
		}
	}

	self.canChangeDate = function(deltaDays) {
		var date = self.bookingDate();
		if(date) {
			date = adjustedDate(self.bookingDate(), deltaDays);
			return isDateDuringEvent(date);
		}
	}

	self.nextDay = function() {
		return self.changeDate(1);
	}

	self.prevDay = function() {
		return self.changeDate(-1);
	}

	self.nextDayEnabled = ko.pureComputed(function() {
		return self.canChangeDate(1);
	});

	self.prevDayEnabled = ko.pureComputed(function() {
		return self.canChangeDate(-1);
	});

	googleMaps.load().then(function() {
		var center = new google.maps.LatLng(-22.50831, 136.078612);
		var zoom = 4;

		// if(initialState.map) {
		// 	var x = initialState.map.split("_");
		// 	center = new google.maps.LatLng(+x[0], +x[1]);
		// 	zoom = +x[2];
		// }

		self.mapOptions(googleMaps.defaultOptions({
			center: center,
			zoom: zoom,
			fullscreenControl: false,
			mapTypeControl: false,
			zoomControl: false
		}));
	});

	function setupMap(map) {
		if(setupMap.called) {
			return;
		} else {
			setupMap.called = true;
		}
		var program = self.program();

		map.overlayMapTypes.clear();
		if(program.mapGrid && program.mapGrid.type == 'mga') {
			setupGridSites(map);
		} else if(program.visibleSharedSiteLayers) {
			setupSharedSites(map);
		}

		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.mapTypeSelectorControl(map));
		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.zoomControl(map));
	}

	function zoomToSite(site) {
		if(self.map()) {
			self.map().fitBounds(siteBounds(site));
		}
	}

	function siteBounds(site, bounds) {
		var sw = new google.maps.LatLng(site.bounds.miny, site.bounds.minx);
		var ne = new google.maps.LatLng(site.bounds.maxy, site.bounds.maxx);
		if(bounds) {
			return bounds.extend(sw).extend(ne);
		} else {
			return new google.maps.LatLngBounds(sw, ne);
		}
	}

	var sitesById = null;
	var hoverOverlay;

	function setupSharedSites(map) {
		var program = self.program();
		var layers = program.visibleSharedSiteLayers();

		if(layers.length == 0) {
			return;
		}

		var sharedSiteLayerPromises = layers.map(layer => birdata.sharedSites({ layer: layer }));
		var sharedSitesPromise = Promise.all(sharedSiteLayerPromises)
			.then(sites => sites.flat(1))
			.then(sites => sites.filter(site => isEventSite(site.id)));

		// Plumbing code for location search
		sharedSitesPromise.then(self.sharedSitesPromiseResolver);

		sharedSitesPromise.then(function(sites) {
			sitesById = {};
			sites.forEach(site => sitesById[site.id] = site);
			selectInitialSite();
		});

		var priorityIcon = {
			path: google.maps.SymbolPath.CIRCLE,
			fillColor: priorityColor,
			fillOpacity: 1,
			strokeWeight: 2,
			strokeColor: "white",
			scale: 6
		};
		var unavailableIcon = {
			path: google.maps.SymbolPath.CIRCLE,
			fillColor: unavailableColor,
			fillOpacity: 1,
			strokeWeight: 2,
			strokeColor: "white",
			scale: 6
		};
		var regularIcon = {
			path: google.maps.SymbolPath.CIRCLE,
			fillColor: nonPriorityColor,
			fillOpacity: 1,
			strokeColor: 'white',
			strokeWeight: 2,
			scale: 6
		};

		var siteMarkers = [];

		function colorForSharedSite(site) {
			var date = self.bookingDate();
			if(!isSiteAvailableOnDate(site.id, date)) {
				return unavailableColor;
			} else if(isPrioritySite(site.id)) {
				return priorityColor;
			} else {
				return nonPriorityColor;
			}
		}

		function iconForSharedSite(site) {
			var date = self.bookingDate();
			if(!isSiteAvailableOnDate(site.id, date)) {
				return unavailableIcon;
			} else if(isPrioritySite(site.id)) {
				return priorityIcon;
			} else {
				return regularIcon;
			}
		}

		var sharedSiteMarkerLayer;

		// Markers
		sharedSitesPromise.then(function(sharedSites) {
			var layer = markerLayer.layer(map, {
				density: 2,
				maxZoom: markerMaxZoom(),
				listeners: {
					click: function() {
						zoomToSite(this.userData.sharedSite);
					}
				}
			});
			layer.setPriorityFn(function(m) {
				var siteID = m.marker.userData.sharedSite.id;
				if(isSiteAvailableOnDate(siteID, self.bookingDate())) {
					return isPrioritySite(siteID) ? 1 : 2;
				} else {
					return 3;
				}
			});

			sharedSites.forEach(sharedSite => {
				var marker = new google.maps.Marker({
					position: { lat: sharedSite.centroid.lat, lng:sharedSite.centroid.lon },
					title: sharedSite.name,
					userData: {
						sharedSite
					},
					icon: iconForSharedSite(sharedSite)
				});
				siteMarkers.push(marker);
				layer.addMarker(marker);
			});
			// Unfortunately this setTimeout seems to be needed to get the markers to reliably appear on first load
			// TODO: investigate
			setTimeout(function() {
				layer.refresh();
			});
			sharedSiteMarkerLayer = layer;
		});

		function updateSiteMarkers() {
			var date = self.bookingDate();
			siteMarkers.forEach(function(marker) {
				var sharedSite = marker.userData.sharedSite;
				marker.setIcon(iconForSharedSite(sharedSite));
			});
			if(sharedSiteMarkerLayer) {
				sharedSiteMarkerLayer.refresh();
			}
		}
		self.bookingDate.subscribe(updateSiteMarkers);

		hoverOverlay = googleMaps.hoverOverlay(map);

		// Polygons
		var sharedSiteLayer = sharedSitesMap.showLayer(map, {
			layerID: "sharedSites",
			outlineWidth: 6,
			minZoom: markerMaxZoom() + 1,
			featureColor: function(feature) {
				var site = sitesById[feature.id];
				if(site) {
					return colorForSharedSite(site);
				} else {
					// SNH
					console.log('Unable to find site with id: ' + feature.id);
					return 'purple';
				}
			},
			polygonEvents: {
				click: function(evt) {
					closeInfoWindow();
					var siteID = this.userData.id;
					showInfoWindow(siteID, 'site', evt.latLng);
				},
				mousemove: function(evt) {
					hoverOverlay.position(evt.latLng);
				},
				mouseover: function(evt) {
					var site = sitesById[this.userData.id];
					if(site) {
						hoverOverlay.html(site.name);
						hoverOverlay.position(evt.latLng);
						hoverOverlay.show();
					}
				},
				mouseout: function(evt) {
					hoverOverlay.hide();
				}
			},
			markerEvents: {
				// TODO
			}
		});
		sharedSiteLayer.setFeatureFilter(function(feature) {
			var attrs = feature.userData && feature.userData.attributes;
			return attrs && layers.includes(attrs.layer) && isEventSite(attrs.id);
		});
		self.bookingDate.subscribe(function() {
			sharedSiteLayer.refresh();
		});

		function selectInitialSite() {
			if(queryParams.site) {
				var site = sitesById[queryParams.site];
				if(site) {
					zoomToSite(site);
					showInfoWindow(queryParams.site, 'site', new google.maps.LatLng(site.centroid.lat, site.centroid.lon));
				}
			} else {
				zoomToFitSites();
			}
		}

		function zoomToFitSites() {
			sharedSitesPromise.then(sharedSites => {
				var bounds = new google.maps.LatLngBounds();
				sharedSites.forEach(sharedSite => {
					bounds.extend({ lat: sharedSite.centroid.lat, lng:sharedSite.centroid.lon })
				})
				if(!bounds.isEmpty()) {
					map.fitBounds(googleMaps.scaleBounds(bounds, 1.5), 10)
				}
			})
		}
	}

	function setupGridSites(map) {
		var priorityIcon = mapStyles.gridMarkerIcon(priorityColor);
		var unavailableIcon = mapStyles.gridMarkerIcon(unavailableColor);

		var program = self.program();
		var event = self.event;

		var gridSize = program.mapGrid.size || 1000;

		var gridOverlay = new UTMGridMapType({
		  tileSize: new google.maps.Size(512, 512),
		  map: map,
		  gridSize: gridSize,
		  minZoom: markerMaxZoom() + 1
		});
		map.overlayMapTypes.push(gridOverlay);

		var siteLayer = markerLayer.layer(map, {
			density: 3,
			maxZoom: markerMaxZoom(),
			listeners: {
				click: handleMarkerClick
			}
		});

		function handleMarkerClick(evt) {
			var id = utmGrid.gridCellIDFromLatLon(evt.latLng.lat(), evt.latLng.lng(), gridSize);
			zoomToGridCell(id, gridSize);
		}

		// Polygons for all priority site and all booked sites
		var cellPolygons = [];
		var cellMarkers = [];

		if(self.prioritySiteIds) {
			var bounds = new google.maps.LatLngBounds();
			self.prioritySiteIds.forEach(id => {
				var centroid = utmGrid.gridCellCentroid(id, gridSize);
				if(centroid) {
					var p = { lat: centroid.lat, lng: centroid.lon };
					var marker = new google.maps.Marker({
						position: p,
						icon: priorityIcon,
						title: id
					});
					google.maps.event.addListener(marker, 'click', handleMarkerClick);
					marker.cellID = id;
					cellMarkers.push(marker);
					siteLayer.addMarker(marker);
					bounds.extend(p);
				}
				var polygon = createCellPolygon(id, priorityColor);
				if(polygon) {
					polygon.setMap(map);
					cellPolygons.push(polygon);
					google.maps.event.addListener(polygon, 'mouseover', function(evt) {
						hideHoverCellPolygon();
					});
				}
			});
			if(!bounds.isEmpty()) {
				map.fitBounds(bounds, 10);
			}
		}

		var bookedCellIds = new Set(Array.from(self.siteAvailability).map(x => x.split(':')[0]));
		bookedCellIds.forEach(id => {
			if(!self.prioritySiteIds.has(id)) {
				var polygon = createCellPolygon(id, unavailableColor);
				cellPolygons.push(polygon);
			}
		});

		function updateCellPolygons() {
			var date = self.bookingDate();
			cellPolygons.forEach(function(polygon) {
				var cellID = polygon.cellID;
				if(!isSiteAvailableOnDate(cellID, date)) {
					polygon.setOptions({
						fillColor: unavailableColor,
						strokeColor: unavailableColor,
						map: map
					});
				} else if(isPrioritySite(cellID)) {
					polygon.setOptions({
						fillColor: priorityColor,
						strokeColor: priorityColor,
						map: map
					});
				} else {
					polygon.setMap(null);
				}
			})
		}
		self.bookingDate.subscribe(updateCellPolygons);
		updateCellPolygons();

		function updateCellMarkers() {
			var date = self.bookingDate();
			cellMarkers.forEach(function(marker) {
				var cellID = marker.cellID;
				if(isSiteAvailableOnDate(cellID, date)) {
					marker.setIcon(priorityIcon);
				} else {
					marker.setIcon(unavailableIcon);
				}
			});
		}
		self.bookingDate.subscribe(updateCellMarkers);
		updateCellMarkers();

		function createCellPolygon(id, colour) {
			var corners = utmGrid.gridCellCorners(id, gridSize);
			if(corners) {
				var path = corners.map(p => ({ lat: p.lat, lng: p.lon }));
				var polygon = new google.maps.Polygon({
					paths: path,
					fillColor: colour,
					strokeColor: colour,
					fillOpacity: 0.3,
					strokeWeight: 2
				});
				polygon.cellID = id;
				return polygon;
			}
		}

		// Dynamic grid cells on hover
		var hoverCellPolygon = null;
		function showHoverCellPolygon(cellID) {
			if(hoverCellPolygon) {
				if(hoverCellPolygon.cellID != cellID) {
					hideHoverCellPolygon();
				} else {
					return;
				}
			}
			if(isEventSite(cellID)) {
				hoverCellPolygon = createCellPolygon(cellID, nonPriorityColor);
				hoverCellPolygon.setMap(map);
				google.maps.event.addListener(hoverCellPolygon, 'click', handleMapClick)
			}
		}

		function hideHoverCellPolygon() {
			if(hoverCellPolygon) {
				hoverCellPolygon.setMap(null);
				hoverCellPolygon = null;
			}

		}
		google.maps.event.addListener(map, 'mousemove', function(evt) {
			if(map.getZoom() <= markerMaxZoom()) {
				hideHoverCellPolygon();
			} else {
				// Determine grid cell
				var cellID = utmGrid.gridCellIDFromLatLon(evt.latLng.lat(), evt.latLng.lng(), gridSize);
				if(isPrioritySite(cellID)) {
					hideHoverCellPolygon();
				} else {
					showHoverCellPolygon(cellID);
				}
			}
		});
		google.maps.event.addListener(map, 'mouseout', function(evt) {
			hideHoverCellPolygon();
		});

		function updateCellVisibility() {
			cellPolygons.forEach(p => p.setVisible(map.getZoom() > markerMaxZoom()));
			if(hoverCellPolygon) {
				hoverCellPolygon.setVisible(map.getZoom() > markerMaxZoom());
			}
		}
		google.maps.event.addListener(map, 'zoom_changed', updateCellVisibility);

		function handleMapClick(evt) {
			closeInfoWindow();
			if(map.getZoom() < markerMaxZoom()) return;
			var p = evt.latLng;
			var cellID = utmGrid.gridCellIDFromLatLon(p.lat(), p.lng(), gridSize);
			showInfoWindow(cellID, 'cell');
		}
		google.maps.event.addListener(map, 'click', handleMapClick);
		cellPolygons.forEach(p => google.maps.event.addListener(p, 'click', handleMapClick));

		function zoomToGridCell(id, gridSize) {
			var cell = utmGrid.parseGridCellID(id);
			var corners = utmGrid.gridCellCorners(id, gridSize);
			if(corners) {
				var bounds = new google.maps.LatLngBounds();
				corners.forEach(p => bounds.extend({ lat: p.lat, lng: p.lon }));
				var span = bounds.toSpan();

				map.fitBounds({
					east: bounds.getNorthEast().lng() + span.lng() / 2,
					west: bounds.getSouthWest().lng() - span.lng() / 2,
					north: bounds.getNorthEast().lat() + span.lat() / 2,
					south: bounds.getSouthWest().lat() - span.lat() / 2
				}, 0);
			}
		}


		if(queryParams.site) {
			zoomToGridCell(queryParams.site, gridSize);
			showInfoWindow(queryParams.site, 'cell');
		}
	}

	ko.computed(function() {
		if(self.map() && self.loaded()) {
			setupMap(self.map());
		}
	});

	// ------- Info window

	var infoWindow;

	function showInfoWindow(siteID, siteType, position) {
		if(siteType == 'cell' && !position) {
			position = utmGrid.gridCellCentroid(siteID, 1000);
			position = { lat: position.lat, lng: position.lon };
		}
		var content = infoWindowContent(siteID, siteType);
		content.addEventListener('mouseover', function() {
			if(hoverOverlay) hoverOverlay.hide();
		});
		closeInfoWindow();
		infoWindow = new google.maps.InfoWindow({
			position: position,
			content: content,
		});
		infoWindow.refreshContent = function() {
			infoWindow.setContent(infoWindowContent(siteID, siteType));
		}
		infoWindow.open(self.map());
	}

	function closeInfoWindow() {
		if(infoWindow) {
			infoWindow.close();
			infoWindow = null;
		}
	}

	self.bookingDate.subscribe(function() {
		if(infoWindow) {
			infoWindow.refreshContent();
		}
	});

	function bookingLink(siteID) {
		return '/book-site?' + util.encodeParams({
			event: eventCode,
			site: siteID,
			date: self.bookingDate()
		});
	}

	function infoWindowContent(siteID, siteType) {
		var siteName;
		if(siteType == 'cell') {
			siteName = siteID;
		} else {
			if(sitesById && sitesById[siteID]) {
				siteName = sitesById[siteID].name;
			} else {
				return;
			}
		}

		var existingBookings = userBookingForSite(siteID);
		var existingBookingsEl;
		if(existingBookings.length > 0) {
			var dates = existingBookings.map(b => util.formatDate(b.date)).join(', ');
			existingBookingsEl = h('p', { 'class': 'bold' }, 'You have booked this site for ' + dates);
		}
		var isAvailable = isSiteAvailableOnDate(siteID, self.bookingDate());

		return h('div', { 'class': 'info-window' }, [
			h('h4', siteName),
			isPrioritySite(siteID) ? h('h5', 'Priority site') : null,
			h('p', 'Availability:'),
			availabilityIndicator(siteID),
			existingBookingsEl,
			isAvailable ? h('a', { 'class': 'button', 'href': bookingLink(siteID) }, 'Book Site') : null
		])
	}

	function handleIndicatorDateClick(evt) {
		var date = evt.currentTarget.getAttribute("data-date");
		if(isDateDuringEvent(date)) {
			self.bookingDate(date);
		}
	}

	function availabilityIndicator(siteID) {
		var currentDate = util.parseISODate(self.bookingDate());
		var dates = [];
		for(var d = -3; d <= 3; d++) {
			var date = new Date(currentDate);
			date.setDate(date.getDate() + d);
			dates.push(date);
		}
		var monthFormatter = new Intl.DateTimeFormat(undefined, { month: 'short' });
		var dayFormatter =  new Intl.DateTimeFormat(undefined, { weekday: 'short' });

		var indicators = dates.map(function(date) {
			var isoDate = util.formatISODate(date);
			var isCurrent = isoDate == self.bookingDate();
			var isDuringEvent = isDateDuringEvent(isoDate);
			var isAvailable = isSiteAvailableOnDate(siteID, isoDate);
			var classList = [];
			if(isCurrent) {
				classList.push('current');
			}
			if(isDuringEvent) {
				if(isAvailable) {
					classList.push('available');
				} else {
					classList.push('unavailable');
				}
			}
			var monthString = monthFormatter.format(date);
			var dayString = dayFormatter.format(date);
			return h('div', { 'class': classList.join(" "), '$click': handleIndicatorDateClick, 'data-date': isoDate }, [
				h('div', { 'class': 'day' }, dayString),
				h('div', { 'class': 'date' }, date.getDate() + ""),
				h('div', { 'class': 'month' }, monthString)
			]);
		});

		var prevClass = 'control' + (self.canChangeDate(-1) ? ' enabled' : '');
		var nextClass = 'control' + (self.canChangeDate(+1) ? ' enabled' : '');

		var prev = h('div', {
			'class': prevClass,
			'$click': function() { self.changeDate(-1); }
		}, '‹');
		var next = h('div', {
			'class': nextClass,
			'$click': function() { self.changeDate(+1); }
		}, '›');

		return h('div', { 'class': 'availability' }, [prev, indicators, next].flat());
	}

	// ------- Location Search

	self.searchPlaceholder = ko.pureComputed(function() {
		return "Search for site or location";
	});

	self.searchResultTypes = ko.pureComputed(function() {
		if(self.program() && self.program().mapGrid) {
			return ['GRID_CELL', 'LOCATION'];
		} else {
			return ['SHARED_SITE', 'LOCATION'];
		}
	});

	self.searchResultSort = function(result) {
		if(self.isGridBased()) {
			// Don't change order
			return 0;
		} else {
			// Shared sites first
			return result.type == 'SHARED_SITE' ? 0 : 1;
		}
	}

	self.locationResultSelected = function(suggestion) {
		var data = suggestion.data;
		var map = self.map();

		switch(suggestion.type) {
		  case 'SHARED_SITE':
		  if(map) {
		    map.fitBounds(googleMaps.bounds(data.bounds));
		    showInfoWindow(data.id, 'site', { lat: data.centroid.lat, lng: data.centroid.lon })
		  }
		  break;
		case 'LOCATION':
			if(map) {
				map.fitBounds(new google.maps.LatLngBounds(
					new google.maps.LatLng(data.ymin, data.xmin),
					new google.maps.LatLng(data.ymax, data.xmax)));
			}
			break;
		case 'GRID_CELL':
			if(map) {
				map.setCenter({lat: data.centroid.lat, lng: data.centroid.lon });
				map.setZoom(15);
				showInfoWindow(data.cellID, 'cell');
			}
			break;
		}
	};

	// ------- End Location Search
};

ko.components.register('booking-map', {
	viewModel: function(params) {
		return new BookingMapViewModel(params);
	},
	template: require('../../templates/booking-map.html')
});

export default BookingMapViewModel;
